import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, type AssessmentRunStatus } from "../../lib/api";
import { activePollMs } from "../../lib/polling";
import { useConfirmDialog } from "../../components/ConfirmDialog.helpers";

import { CollapsibleSection } from "./shared";
import { AssessmentRunReportCard } from "./AssessmentRunReportCard";
import { RunHistoryPanel } from "./RunHistoryPanel";

export function Phase3AssessmentsCard({
  autoRunInFlight,
}: {
  autoRunInFlight: boolean;
}) {
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  // Task #326 — Re-hydrate the local activeRunId from the server when the
  // runner remounts (e.g. user navigated to Teams/Agents and back). Without
  // this, the in-progress UI and Cancel run button silently disappear even
  // though the run is still going on the worker.
  const activeRunQ = useQuery({
    queryKey: ["assessment-run-active"],
    queryFn: () => api.assessmentRunsActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
  });

  // Ticket 14 — Super-Admin LLM model dropdown. The selected value
  // persists in ``app_settings."assessments.llm_model"`` and is the
  // default used for auto-chained Phase 3 runs at the tail of every
  // Phase 2 sync.
  const modelsQ = useQuery({
    queryKey: ["assessment-runs-allowed-models"],
    queryFn: () => api.assessmentRunsAllowedModels(),
    placeholderData: keepPreviousData,
  });

  const setModelM = useMutation({
    mutationFn: async (model: string) =>
      api.assessmentRunsSetDefaultModel(model),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment-runs-allowed-models"] });
    },
  });
  const setModelErr = setModelM.error as { message?: string } | null;

  // Ticket 15 — Super-Admin concurrency controls. The persisted cap
  // governs every run. The cap is clamped server-side to
  // [1, assessment_run_global_concurrency] so a typo can't starve the
  // worker.
  const runSettingsQ = useQuery({
    queryKey: ["assessment-run-settings"],
    queryFn: () => api.assessmentRunSettings(),
    placeholderData: keepPreviousData,
  });
  const setRunSettingsM = useMutation({
    mutationFn: async (concurrency: number) =>
      api.assessmentRunSettingsUpdate({ concurrency }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment-run-settings"] });
    },
  });
  const setRunSettingsErr =
    setRunSettingsM.error as { message?: string } | null;
  const [editingDefault, setEditingDefault] = useState(false);
  const [concurrencyDraft, setConcurrencyDraft] = useState<string>("");
  const maxConcurrency = runSettingsQ.data?.max_concurrency ?? 25;

  const cancelM = useMutation({
    mutationFn: async (runId: string) =>
      api.assessmentRunCancel(runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-status"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-latest"] });
      qc.invalidateQueries({ queryKey: ["assessment-runs-history"] });
    },
  });
  const cancelErr = cancelM.error as { message?: string } | null;

  async function handleCancelClick() {
    // Drive cancel off the active run id rather than the live status
    // field — a transient status poll error or a momentary non-active
    // status flip from the worker side must not strand the operator
    // without an in-app way to stop a run.
    const runId = activeRunId;
    if (!runId) return;
    const ok = await dialog.confirm({
      title: "Cancel this assessment run?",
      message:
        "The run will stop after the current pair finishes. Results already graded are kept; remaining pairs will not be processed.",
      confirmLabel: "Cancel run",
      destructive: true,
    });
    if (!ok) return;
    try {
      await cancelM.mutateAsync(runId);
    } catch {
      /* error surfaced via cancelErr */
    }
  }

  const runStatusQ = useQuery({
    queryKey: ["assessment-run-status", activeRunId],
    queryFn: () => api.assessmentRunStatus(activeRunId!),
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      // Only stop polling on a *confirmed* terminal state. Any other
      // value — including transient errors, unknown status strings, or
      // a status field that briefly disappears — keeps the 2s cadence
      // so the cancel button stays reachable and the panel keeps
      // showing last-known progress.
      if (
        s === "completed" ||
        s === "failed" ||
        s === "partial" ||
        s === "cancelled"
      )
        return false;
      return 2000;
    },
    // Keep last-good payload across transient errors / network blips so
    // the "Running…" counters and the Cancel button don't flicker when
    // a single poll fails. retry covers 401/5xx by default.
    placeholderData: keepPreviousData,
    retry: true,
    enabled: !!activeRunId,
  });

  // Surface the most recently created assessment_run job — auto-chained
  // by every sync — so the run report doesn't disappear when the
  // active-sync card vanishes after the sync completes. Poll on a
  // slower cadence than runStatusQ since this is a "background" query.
  const latestRunQ = useQuery({
    queryKey: ["assessment-run-latest"],
    queryFn: () => api.assessmentRunsLatest(),
    refetchInterval: () =>
      activeRunId || activeRunQ.data?.has_active ? 3000 : 30000,
    placeholderData: keepPreviousData,
  });

  // Source for the in-card "Running…" panel.
  const runStatus = runStatusQ.data;
  // Treat these as the only confirmed terminal states. Everything else —
  // unknown status strings, transient errors that flip the worker's view
  // of the row to "failed"/"dead" while it's still actually executing,
  // missing status fields — keeps the run treated as active so the
  // cancel control and progress panel stay visible.
  const isTerminalStatus =
    runStatus?.status === "completed" ||
    runStatus?.status === "failed" ||
    runStatus?.status === "partial" ||
    runStatus?.status === "cancelled";
  const isRunActive = !!activeRunId && !isTerminalStatus;

  // Task #326 — When the runner remounts (after navigating away and back)
  // and we have no local activeRunId, adopt the server's reported active
  // run_id so the status poll, progress UI, and Cancel run button light up
  // again. Only do this while the server-reported run is still non-terminal;
  // never clobber a freshly-started local id (the `!activeRunId` guard).
  useEffect(() => {
    if (activeRunId) return;
    const d = activeRunQ.data;
    if (d?.has_active && d.run_id) {
      setActiveRunId(d.run_id);
    }
  }, [activeRunId, activeRunQ.data]);

  // Task #326 — Clear the local id once the run is truly finished. Two
  // triggers: the server's active-run endpoint flips has_active=false, or
  // the status poll for our id reaches a confirmed terminal state. Both
  // are gated to avoid wiping out a just-started local id whose status
  // hasn't been polled yet (no runStatus + active query still catching up).
  useEffect(() => {
    if (!activeRunId) return;
    const serverIdle =
      activeRunQ.data && activeRunQ.data.has_active === false;
    if (isTerminalStatus || (serverIdle && !!runStatus)) {
      setActiveRunId(null);
    }
  }, [activeRunId, activeRunQ.data, isTerminalStatus, runStatus]);

  // Hold onto the last progress payload we saw so the in-card counters
  // don't flash back to zero on a transient status-poll error or while
  // we're between polls. The status query itself uses
  // ``placeholderData: keepPreviousData`` to dampen flicker, but a hard
  // error still surfaces ``data === undefined``; this state survives
  // that case.
  const [lastGoodProgress, setLastGoodProgress] = useState<
    AssessmentRunStatus["progress"] | null
  >(null);
  useEffect(() => {
    if (runStatus?.progress) {
      setLastGoodProgress(runStatus.progress);
    }
    if (!activeRunId) {
      setLastGoodProgress(null);
    }
  }, [runStatus?.progress, activeRunId]);
  const runProgress = runStatus?.progress ?? lastGoodProgress;
  const statusPollErr = runStatusQ.error as { message?: string } | null;

  // Source for the post-completion run-summary block: prefer the active
  // run when it's done; otherwise show the most recently created
  // assessment_run job in the system (which catches auto-chained runs
  // after the active-sync card vanishes).
  const manualDone =
    runStatus?.status === "completed" ||
    runStatus?.status === "failed" ||
    runStatus?.status === "partial" ||
    runStatus?.status === "cancelled";
  const reportRun = manualDone ? runStatus : (latestRunQ.data ?? null);
  const isRunDone =
    reportRun?.status === "completed" ||
    reportRun?.status === "failed" ||
    reportRun?.status === "partial" ||
    reportRun?.status === "cancelled";

  const assessmentsAutoExpand =
    isRunActive ||
    autoRunInFlight ||
    reportRun?.status === "failed" ||
    reportRun?.status === "partial";

  return (
    <CollapsibleSection
      title="Assessment Runner"
      ariaLabel="Assessment Runner"
      subtitle="Configure how assessments run and review past runs. Assessments run automatically at the end of every Sync as Phase 3."
      autoExpand={assessmentsAutoExpand}
    >
      <div className="p-4 space-y-3">
      {/* Ticket 15 — persistent default concurrency. Inline display +
          Edit affordance that flips into a number input with Save /
          Cancel. No overlay modal. */}
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        Default concurrency:{" "}
        {runSettingsQ.data?.is_persisted ? (
          <span className="text-foreground tabular-nums">
            {runSettingsQ.data.concurrency}
          </span>
        ) : (
          <span className="text-foreground tabular-nums">
            {runSettingsQ.data?.default_concurrency ?? "—"}
            <span className="text-muted-foreground"> (env default)</span>
          </span>
        )}
        {!editingDefault ? (
          <button
            type="button"
            onClick={() => {
              setConcurrencyDraft(
                runSettingsQ.data?.is_persisted
                  ? String(runSettingsQ.data.concurrency)
                  : String(
                      runSettingsQ.data?.default_concurrency ?? "",
                    ),
              );
              setEditingDefault(true);
            }}
            className="text-foreground hover:underline"
          >
            Edit
          </button>
        ) : (
          <>
            <span aria-hidden="true">·</span>
            <input
              type="number"
              min={1}
              max={maxConcurrency}
              value={concurrencyDraft}
              onChange={(e) => setConcurrencyDraft(e.target.value)}
              className="border border-input rounded px-2 py-1.5 text-sm w-20 tabular-nums"
              aria-label="Default per-job concurrency"
            />
            <button
              type="button"
              onClick={async () => {
                const trimmed = concurrencyDraft.trim();
                const parsed = Number.parseInt(trimmed, 10);
                if (!Number.isFinite(parsed) || parsed < 1) return;
                const next = Math.min(maxConcurrency, parsed);
                try {
                  await setRunSettingsM.mutateAsync(next);
                  setEditingDefault(false);
                } catch {
                  /* surfaced via setRunSettingsErr */
                }
              }}
              disabled={setRunSettingsM.isPending}
              className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
            >
              {setRunSettingsM.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditingDefault(false)}
              className="text-muted-foreground hover:text-foreground hover:underline"
            >
              Cancel
            </button>
          </>
        )}
      </div>
      {setRunSettingsErr && (
        <div className="text-xs text-destructive">
          Couldn't save concurrency: {setRunSettingsErr.message}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-muted-foreground">
          LLM model
          <select
            value={modelsQ.data?.current ?? ""}
            onChange={(e) => setModelM.mutate(e.target.value)}
            disabled={
              modelsQ.isLoading ||
              setModelM.isPending ||
              (modelsQ.data?.models.length ?? 0) === 0
            }
            className="w-full border border-input rounded px-2 py-1.5 text-sm"
            title="The model used for every assessment run — the Phase 3 auto-chain at the end of every sync."
          >
            {modelsQ.isLoading ? (
              <option value="">Loading…</option>
            ) : (modelsQ.data?.models.length ?? 0) === 0 ? (
              <option value="">No models configured</option>
            ) : (
              (modelsQ.data?.models ?? []).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))
            )}
          </select>
          <span className="text-[11px] text-muted-foreground mt-0.5">
            {setModelM.isPending
              ? "Saving…"
              : "Default for Phase 3 auto-chain."}
          </span>
        </label>
      </div>
      {setModelErr && (
        <div className="text-xs text-destructive">
          Couldn't save model: {setModelErr.message}
        </div>
      )}

      {isRunActive && (
        <div className="rounded border border-border bg-muted/30 p-3 text-sm flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              {runProgress ? (
                <>
                  Running:{" "}
                  <span className="tabular-nums">{runProgress.completed}</span>{" "}
                  of{" "}
                  <span className="tabular-nums">{runProgress.total}</span>{" "}
                  pairs complete ·{" "}
                  <span className="tabular-nums">{runProgress.failed}</span>{" "}
                  failed
                </>
              ) : (
                <>Run in progress — waiting for first status update…</>
              )}
            </div>
            <button
              type="button"
              onClick={handleCancelClick}
              disabled={cancelM.isPending}
              className="rounded border border-destructive text-destructive text-sm px-3 py-1.5 hover:bg-destructive/10 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
            >
              {cancelM.isPending ? "Cancelling…" : "Cancel run"}
            </button>
          </div>
          {statusPollErr && (
            <div className="text-xs text-muted-foreground">
              Last status update failed ({statusPollErr.message ?? "error"}) —
              retrying. Cancel still available.
            </div>
          )}
        </div>
      )}
      {cancelErr && (
        <div className="text-xs text-destructive">{cancelErr.message}</div>
      )}

      {reportRun && isRunDone && (
        <AssessmentRunReportCard
          run={reportRun}
          hasActiveRun={!!activeRunQ.data?.has_active || isRunActive}
        />
      )}

      <RunHistoryPanel activeRunId={activeRunId} />
      </div>
      {dialog.element}
    </CollapsibleSection>
  );
}
